import { find, flatten, get, isEmpty, isNaN, findIndex, omit, reduce, some } from 'lodash-es';
import moment from 'moment';

import { addFieldsToObject, getSortedExtraData, getQuotationWithProduct } from './transform';
import { getOfferFromV2 } from './transformOfferToV1';
import { getDurationInMonths, convertDurationMonthsInUnit } from '../../Utils/Utils';

const STATUS_MAPPING_TO_QUOTATION = {
  CLOSED: 'canceled',
  PUTONHOLD: 'pending',
  SUBSCRIBED: 'submitted',
  COMPUTED: 'project',
  CREATED: 'project',
  PRESIMULATED: 'project',
  PRESUBSCRIBED: 'project',
  PRINTED: 'project',
  SIMULATED: 'project',
  default: 'project',
};

const getCoveredIndex = (covered, ref) => findIndex(covered, cov => get(cov, 'party.ref') === ref);

const findParty = (parties, ref) => find(parties, p => p.ref === ref);

/**
 * Get agent from id if available or from calc
 */
const getAgent = ({ contracts: [{ agent: { id } = {} } = { agent: {} }] = [{ agent: {} }] }, calc) => {
  if (id) return id;

  if (calc) {
    const { agents = {} } = calc;
    // eslint-disable-next-line no-restricted-syntax
    for (const { id: agentId } of Object.values(agents)) {
      if (agentId != null) return agentId;
    }
  }

  return 0;
};

/**
 * Get calc with sorted extra data and isLoan information
 */
const getCalcFromSalesRoute = ({ isLoan = false, covered: calcCovered = {}, ...calc }) => {
  const covered = getSortedExtraData(calcCovered);

  if (isLoan) {
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of Object.entries(covered)) {
      covered[key] = { ...value, loan: true };
    }
  }

  return { contract: { ...calc, isLoan, covered } };
};

/**
 * Get covereds list from each covered of each contract with party object
 */
const getCoveredFromSalesRoute = ({ contracts = [], parties }, { isLoan = false }) =>
  flatten(
    contracts.map(aContract =>
      get(aContract, 'covereds', []).map(
        ({
          coverages = [],
          extra_data: extra,
          package: { code } = {},
          item_descriptor: { code: type } = {},
          party: { ref } = {},
        }) => {
          let coverage;
          let share = 100;

          coverages.forEach(
            ({
              coverage: { code: covCode } = {},
              loan_shares: [{ share: loanShare } = {}] = [{}],
              extra_data: extraData,
              coverage_amount: covAmount,
            }) => {
              if (!coverage) {
                coverage = {};
              }
              coverage[covCode] = { selected: true };

              if (extraData) coverage[covCode].extra = extraData;
              if (isLoan) share = parseFloat(loanShare) * 100;
              if (covAmount) coverage[covCode].coverage_amount = parseFloat(covAmount);
            }
          );

          const newCovered = {
            coverage,
            extra,
            party: omit(findParty(parties, ref), 'relations', 'addresses', 'bank_accounts', 'identifiers'),
            type,
          };

          if (isLoan) newCovered.share = share;
          if (code) newCovered.package = code;

          return newCovered;
        }
      )
    )
  );

/**
 * Get commercial product from state if available
 */
const getCommercialProductFromSalesRoute = (
  { contracts: [{ commercial_product: { id, code } = {} }] = [{}] },
  state
) => {
  const comProduct = state
    ? state
        .getIn(['commercialproducts'])
        .toJS()
        .find(cp => (code ? cp.code === code : get(cp, 'id') === id))
    : {};
  return { id: get(comProduct, 'id'), name: get(comProduct, 'name') };
};

/**
 * Create a loan object
 */
const createLoan = ({ amount = 0, rate, funds_release_date: releaseDate, increments, ...loan }) => {
  const newLoan = omit(
    {
      ...loan,
      amount: parseFloat(amount),
      release_date: releaseDate,
    },
    'ref'
  );

  if (!isNaN(parseFloat(rate))) {
    newLoan.rate = parseFloat(rate);
  }

  if (increments) {
    newLoan.increments = increments.map(i => ({
      ...i,
      number_of_payments: `${get(i, 'number_of_payments')}`,
      payment_amount: parseFloat(get(i, 'payment_amount', 0)),
    }));
  }

  // Add duration to loan for graduated loans
  if (newLoan.kind === 'graduated') {
    const durationMonth = reduce(
      newLoan.increments,
      (acc, incr) =>
        acc +
        getDurationInMonths({
          duration: parseInt(incr.number_of_payments, 10),
          durationUnit: incr.payment_frequency,
        }),
      0
    );
    newLoan.duration = convertDurationMonthsInUnit({ duration: durationMonth, durationUnit: loan.duration_unit });
  }

  return newLoan;
};

const getLoanFromSalesRoute = ({ loans = [] }) => loans.map(createLoan);

/**
 * Create a party object
 */
const createParty = ({
  email,
  phone,
  is_person: isPerson,
  name,
  birth_date: birthDate,
  first_name: firstName,
  gender,
  siren,
  siret,
}) => {
  const newParty = {
    email,
    phone,
    party: {
      is_person: isPerson,
      name,
    },
  };

  newParty.party = addFieldsToObject(newParty.party, [
    { name: 'birth_date', value: birthDate },
    { name: 'first_name', value: firstName },
    { name: 'gender', value: gender },
    { name: 'siren', value: siren },
    { name: 'siret', value: siret },
  ]);

  return newParty;
};

/**
 * If it's a loan, add a subscription per contract with covered index.
 * If there's a subscriber, add it to the subscription with covered index or party if no covered.
 */
const getSubscriptionsFromSalesRoute = ({ contracts = [], parties = [] }, covered, isLoan, calc) => {
  const subscriptions = [];

  if (isLoan) {
    contracts.forEach(
      ({
        covereds = [],
        covereds: [{ party: { ref: partyRef } } = { party: {} }] = [{ party: {} }],
        subscriber: { ref } = {},
        billing: {
          direct_debit_day: directDebitDay,
          billing_mode: { id: billingModeId } = {},
          bank_account_number: iban,
        } = { billing_mode: {} },
      }) => {
        if (partyRef) {
          const idxCov = covereds.length === 1 ? getCoveredIndex(covered, partyRef) : 0;
          const subscriber = findParty(parties, ref);

          let newSubscription = {
            covered: idxCov,
            direct_debit: get(find(calc.billingModes, { id: billingModeId }), 'direct_debit', true),
          };

          if (directDebitDay) newSubscription.allowed_direct_debit_day = directDebitDay;
          if (billingModeId) newSubscription.billing_mode = billingModeId;
          if (iban !== null && iban !== undefined) newSubscription.iban = iban;

          if (subscriber) {
            const { addresses, ref: refSub } = subscriber;

            const idxSub = getCoveredIndex(covered, refSub);
            if (idxSub < 0) newSubscription = { ...newSubscription, ...createParty(subscriber) };

            if (addresses && addresses.length) newSubscription.addresses = addresses;
            newSubscription.subscriber = idxSub;
          }

          subscriptions.push(newSubscription);
        }
      }
    );
  }

  return subscriptions;
};

/**
 * Iterate through contracts to get a transform and
 * add covered index or party if no covered.
 * Not ideal though, if one day there's more than 1 contract...
 */
const getTransformFromSalesRoute = (
  { contracts = [], contracts: [{ covereds }] = [[]], parties = [] },
  covered,
  isLoan,
  calc
) => {
  let transform = {};

  if (!isLoan) {
    contracts.forEach(
      ({
        subscriber: { ref } = {},
        billing: {
          direct_debit_day: directDebitDay,
          billing_mode: { id: billingModeId } = {},
          bank_account_number: iban,
        } = { billing_mode: {} },
      }) => {
        const subscriber = findParty(parties, ref);

        if (covereds.length) {
          transform.direct_debit = get(find(calc.billingModes, { id: billingModeId }), 'direct_debit', true);
          if (directDebitDay) transform.allowed_direct_debit_day = directDebitDay;
          if (billingModeId) transform.billing_mode = billingModeId;
          if (iban !== null && iban !== undefined) transform.iban = iban;

          if (subscriber) {
            const { addresses, ref: refSub } = subscriber;
            const idxSub = getCoveredIndex(covered, refSub);

            if (addresses && addresses.length) transform.addresses = addresses;

            if (idxSub < 0) {
              transform = { ...transform, ...createParty(subscriber) };
            } else {
              transform.subscriber = idxSub;
            }
          }
        }
      }
    );
  }

  return transform;
};

/**
 * Transform a SalesRoute to a Quotation
 * Will get all mandatory fields and optional fields if available or
 * not empty (calc, subscriptions, fundsReleaseDate, extra, offer).
 * Will get covereds list from each contract.
 * Will get subscriptions from each contract with covered index and add them to the quotation if there are some.
 * Will get commercial product from state if available.
 * Will transform loans to loan.
 * Will get transform from each contract.
 * Will transform calc and add isLoan.
 * Will transform offerV2 to offer V1 format.
 *
 * @param {object} item SalesRoute
 * @param {object} calc Calc object
 * @param {object} state State to get CommercialProduct
 * @return {object} Quotation
 */
export const getQuotationFromSalesRoute = (item, calc, state) => {
  try {
    const {
      dist_network: distNetwork,
      createdAt,
      contracts,
      contracts: [
        {
          commercial_product: { id: comProdId, comProdCode } = {},
          start,
          extra_data: extra,
          product: { id: productId } = {},
        } = { product: {} },
      ] = [{ product: {} }],
      loans: [{ funds_release_date: fundsReleaseDate } = {}] = [{}],
      _id: id,
      status,
      updatedAt,
      parties,
    } = item;
    const { isLoan = false } = calc;

    const covered = getCoveredFromSalesRoute(item, calc);
    const subscriptions = getSubscriptionsFromSalesRoute(item, covered, isLoan, calc);

    const productIdFromComProd = state
      ?.getIn(['commercialproducts'])
      ?.toJS()
      ?.find(cp => (comProdCode ? cp?.code === comProdCode : cp?.id === comProdId))?.product;
    const contractIds = contracts.map(contract => contract?.id)
    const quoteNumbers = contracts.map(contract => contract?.quote_number)
    const quotation = {
      agent: getAgent(item, calc),
      broker: distNetwork,
      commercial_product: getCommercialProductFromSalesRoute(item, state),
      contracts: contractIds,
      quoteNumbers,
      coverage: {},
      covered: covered.map(cov => ({ ...cov, party: omit(cov.party, 'ref') })),
      createdOn: moment(createdAt)
        .utc()
        .format('YYYY-MM-DD HH:mm:ss'),
      date: start,
      discounts: [],
      duration: null,
      id,
      loan: getLoanFromSalesRoute(item),
      product: productId || productIdFromComProd,
      quote_status: get(STATUS_MAPPING_TO_QUOTATION, status, STATUS_MAPPING_TO_QUOTATION.default),
      transform: getTransformFromSalesRoute(item, covered, isLoan, calc),
      writeDate: moment(updatedAt)
        .utc()
        .format('YYYY-MM-DD HH:mm:ss'),
    };

    if (calc) quotation.calc = getCalcFromSalesRoute(calc);
    if (subscriptions && subscriptions.length > 0) quotation.subscriptions = subscriptions;
    if (fundsReleaseDate) quotation.release_date = fundsReleaseDate;
    if (extra && !isEmpty(extra)) quotation.extra = extra;
    if (some(item?.contracts, 'offer')) quotation.offer = getOfferFromV2(item?.contracts, parties, item.loans);

    return quotation;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }

  return {};
};

export const getQuotationWithProductFromSR = (data, state) => {
  let calc = state.getIn(['productsCalc', get(data, 'contracts[0].product.id')]);
  calc = calc ? calc.toJS() : null;
  const newQuot = getQuotationFromSalesRoute(data, calc, state);

  return getQuotationWithProduct(newQuot, state);
};
